import { emptyPromptV2, push, pushField } from './compile';
import { htmlToText, parseAuthoredSections } from './parse';
import type { PromptSectionKey, PromptV2 } from './types';

/**
 * Type adapters — parse-and-adopt.
 *
 * THE RULE: whatever the author already wrote wins. 15 of 23 Boston agents
 * carry a hand-authored five-section prompt in `instructions`. The old builders
 * pasted that authored prompt and then appended their own tone preset, their
 * own behaviour guidelines and their own guardrails on top — so the avatar
 * received the author's Tone *and* a competing platform Tone. That is the
 * contradiction Anam warned about, and it is the real cause of the reported
 * inconsistency.
 *
 * So: adopt authored sections verbatim, and let platform defaults fill ONLY
 * the sections the author left empty. Never both.
 */

export interface CoachingTemplateLike {
  name?: string | null;
  category?: string | null;
  description?: string | null;
  output_document?: string | null;
  system_prompt?: string | null;
  intake_script?: unknown;
}

export interface AdapterContext {
  template?: CoachingTemplateLike | null;
  /** Concise prior-session learner context. Informs the coach; never overrides behaviour. */
  memorySummary?: string | null;
  /**
   * Passages retrieved for this session (RAG). V2 never pastes whole documents:
   * the legacy builder capped each source at 6,000 chars, so 58% of FARAPOINT
   * Coach's knowledge was excluded from the live prompt at runtime.
   */
  knowledgeExtracts?: string[];
}

// ---------------------------------------------------------------- tone ----
// Compact descriptors replacing the old ~30-line preset blocks. Gap-fill only.

const CONVERSATION_TONE: Record<string, string> = {
  warm_professional: 'Warm, personable and professionally engaged. Friendly but never unprofessional.',
  neutral: 'Calm, measured and matter-of-fact. Neither warm nor cold — efficient and professional.',
  empathetic: 'Compassionate and emotionally attuned. Acknowledge feelings before facts.',
  skeptical: 'Questioning. You require evidence before being convinced, but you are not hostile.',
  dismissive: 'Busy, distracted and unimpressed. Not rude, but clearly preoccupied.',
  anxious: 'Nervous and unsure. Cooperative, but you need patience and reassurance.',
  overconfident: 'Assured to the point of overestimating yourself. You resist correction.',
  aggressive: 'Frustrated and confrontational. Forceful and emotionally charged, never violent.',
};

const EXAMINER_TONE: Record<string, string> = {
  osce_standard: 'Neutral, calm and professional. Standardised delivery — treat every candidate identically.',
  strict: 'Formal and matter-of-fact. Offer no encouragement or reassurance.',
  supportive: 'Warm professional and encouraging, without giving anything away.',
  prompt_limited: 'Neutral and consistent. Do not prompt or lead the candidate.',
  safety_first: 'Direct, professional and serious, especially on patient-safety matters.',
  communication_skills: 'Neutral-to-warm and observational.',
  clinical_reasoning: 'Neutral and inquisitive. Probe the reasoning behind answers.',
};

const COACHING_TONE: Record<string, string> = {
  consultative: 'Collaborative and supportive. Ask open questions; guide through active listening and reflection.',
  challenger: 'Direct and provocative where useful. Challenge assumptions and surface blind spots.',
  socratic: 'Socratic. Lead to insight through strategic questions rather than answers.',
};

const DISCLOSURE: Record<string, string> = {
  progressive: 'Reveal information gradually, as it is asked for. Do not dump everything at once.',
  guarded: 'Share little unless directly and specifically asked.',
  open: 'Share relevant information readily when the topic comes up.',
  reluctant: 'Share only under persistent, well-directed questioning.',
};

/** Legacy tone presets — ONLY used when the author wrote no Tone section. */
function defaultTone(agent: Record<string, any>): string[] {
  const lines: string[] = [];
  const type = agent.type;

  if (type === 'examiner') {
    lines.push(EXAMINER_TONE[agent.examiner_personality] ?? EXAMINER_TONE.osce_standard);
  } else if (type === 'coach') {
    lines.push(COACHING_TONE[agent.coaching_style] ?? COACHING_TONE.consultative);
  } else {
    lines.push(CONVERSATION_TONE[agent.conversation_tone] ?? CONVERSATION_TONE.warm_professional);
  }

  if (agent.disclosure_style && DISCLOSURE[agent.disclosure_style]) lines.push(DISCLOSURE[agent.disclosure_style]);

  const legacy = htmlToText(agent.communication_style);
  if (legacy) lines.push(legacy);

  lines.push('Speak in short, natural turns (1–3 sentences typically) unless asked for more.');
  return lines;
}

// ---------------------------------------------------------- guardrails ----

function defaultGuardrails(personaName: string): string[] {
  return [
    `Never break character. You are ${personaName}. Never say or imply that you are an AI, a model, or a simulation.`,
    'Speak only the words you say aloud. Never output stage directions, bracketed actions, or descriptions of your own tone or pacing — the voice system handles delivery.',
    'If you do not know something, say so in character rather than inventing facts.',
  ];
}

// --------------------------------------------------------------- goal -----

/** Condense the intake script into an ordered checklist (never raw JSON). */
export function intakeChecklist(template: CoachingTemplateLike | null | undefined): string[] {
  const script: any = template?.intake_script;
  const phases: any[] = Array.isArray(script?.phases) ? script.phases : [];
  if (phases.length === 0) return [];

  const lines: string[] = [];
  phases.forEach((phase, i) => {
    const name = htmlToText(phase?.name) || `Stage ${i + 1}`;
    const questions: any[] = Array.isArray(phase?.questions) ? phase.questions : [];
    const asks = questions.map((q) => htmlToText(q?.question)).filter(Boolean);
    if (asks.length === 0) return;
    lines.push(`${i + 1}. ${name}\n${asks.map((a) => `   - ${a}`).join('\n')}`);
  });
  return lines;
}

// ----------------------------------------------------------- defaults -----
// Per-type gap fillers. Each returns lines for ONE section, used only when the
// author left that section empty.

type Filler = (agent: Record<string, any>, ctx: AdapterContext, persona: string) => string[];

const DEFAULTS: Record<string, Partial<Record<PromptSectionKey, Filler>>> = {
  patient: {
    personality: (a, _c, persona) => {
      const lines = [`You are ${persona}, a patient.`];
      if (a.age) lines.push(`Age: ${a.age}`);
      if (a.gender) lines.push(`Gender: ${htmlToText(a.gender)}`);
      if (a.occupation) lines.push(`Occupation: ${htmlToText(a.occupation)}`);
      if (a.personality) lines.push(`Character: ${htmlToText(a.personality)}`);
      if (a.backstory) lines.push(`Background: ${htmlToText(a.backstory)}`);
      return lines;
    },
    environment: (a) => {
      const lines: string[] = [];
      if (a.scenario_context) lines.push(`Consultation context: ${htmlToText(a.scenario_context)}`);
      if (a.presenting_complaint) lines.push(`You are presenting with: ${htmlToText(a.presenting_complaint)}`);
      if (a.medical_history) lines.push(`Your medical history: ${htmlToText(a.medical_history)}`);
      return lines;
    },
    goal: (a) => {
      const lines = [
        'Be a realistic patient in this consultation. Let the clinician lead.',
        'Answer what you are asked. Do not present a tidy summary of your own case.',
      ];
      const gated: string[] = [];
      if (a.presenting_complaint_after_consultation) gated.push('the details of your complaint');
      if (a.medical_history_after_consultation) gated.push('your medical history');
      if (a.physical_assessment_after_consultation) gated.push('physical examination findings');
      if (a.diagnostic_tests_after_consultation) gated.push('test results');
      if (gated.length) {
        lines.push(`Withhold ${gated.join(', ')} until the clinician has examined you or asked directly. If they ask directly, answer honestly.`);
      }
      return lines;
    },
    guardrails: (_a, _c, persona) => [
      ...defaultGuardrails(persona),
      'Do not diagnose yourself or use clinical terminology you would not plausibly know.',
    ],
  },

  customer: {
    personality: (a, _c, persona) => {
      const lines = [`You are ${persona}${a.prospect_role ? `, ${htmlToText(a.prospect_role)}` : ''}.`];
      if (a.occupation) lines.push(`Role: ${htmlToText(a.occupation)}`);
      if (a.personality) lines.push(`Character: ${htmlToText(a.personality)}`);
      if (a.backstory) lines.push(`Background: ${htmlToText(a.backstory)}`);
      return lines;
    },
    environment: (a) => {
      const lines: string[] = [];
      if (a.company_organization) lines.push(`Organisation: ${htmlToText(a.company_organization)}`);
      if (a.industry) lines.push(`Industry: ${htmlToText(a.industry)}`);
      if (a.scenario_context) lines.push(`Situation: ${htmlToText(a.scenario_context)}`);
      if (a.budget_authority) lines.push(`Your purchasing authority: ${htmlToText(a.budget_authority)}`);
      return lines;
    },
    goal: (a) => {
      const lines = ['Behave as this professional genuinely would. Do not speak first — wait for the representative to open.'];
      if (a.roleplay_focus) lines.push(`Focus of this conversation: ${htmlToText(a.roleplay_focus)}`);
      const pains = htmlToText(a.pain_points);
      const objections = htmlToText(a.common_objections);
      const criteria = htmlToText(a.buying_criteria);
      if (pains) lines.push(`If the representative asks good discovery questions, gradually reveal these concerns: ${pains}`);
      if (objections) lines.push(`If they pitch before understanding your situation, raise these objections: ${objections}`);
      if (criteria) lines.push(`You are persuaded only when these criteria are addressed: ${criteria}`);
      lines.push('If they listen well and address your concerns, show gradual interest. If they are pushy or irrelevant, disengage and move to end the call.');
      return lines;
    },
    guardrails: (_a, _c, persona) => [
      ...defaultGuardrails(persona),
      'Do not volunteer your concerns unprompted — make them ask the right questions.',
    ],
  },

  examiner: {
    personality: (a, _c, persona) => {
      const lines = [`You are ${persona}, an examiner.`];
      if (a.clinical_specialty) lines.push(`Specialty: ${htmlToText(a.clinical_specialty)}`);
      if (a.backstory) lines.push(`Background: ${htmlToText(a.backstory)}`);
      return lines;
    },
    environment: (a) => {
      const lines: string[] = [];
      if (a.examination_type) lines.push(`Examination type: ${htmlToText(a.examination_type)}`);
      if (a.station_title) lines.push(`Station: ${htmlToText(a.station_title)}`);
      if (a.case_summary) lines.push(`Case: ${htmlToText(a.case_summary)}`);
      return lines;
    },
    goal: (a) => {
      const lines = ['Conduct this examination. Ask the questions below in order, listening fully before moving on.'];
      if (a.key_questions) lines.push(`Questions to ask, in order:\n${htmlToText(a.key_questions)}`);
      if (a.followup_probes) lines.push(`Use these probes to test depth when an answer is thin:\n${htmlToText(a.followup_probes)}`);
      lines.push('When the questions are covered, close the station politely without commenting on performance.');
      return lines;
    },
    guardrails: (_a, _c, persona) => [
      // Most important first: this is an assessment.
      'Never reveal, hint at, confirm, correct or grade the expected answer. Do not indicate whether the candidate is right or wrong. Ask, listen, and move on.',
      ...defaultGuardrails(persona),
      'Do not teach or coach during the examination.',
    ],
  },

  coach: {
    personality: (a, _c, persona) => {
      const role = htmlToText(a.occupation) || 'professional coach';
      const lines = [`You are ${persona}, ${role}.`];
      if (a.personality) lines.push(`Character: ${htmlToText(a.personality)}`);
      return lines;
    },
    environment: (a) => {
      const lines: string[] = [];
      const focus = htmlToText(a.roleplay_focus || a.scenario_context);
      if (focus) lines.push(`Focus of this session: ${focus}`);
      return lines;
    },
    goal: (_a, ctx) => {
      const checklist = intakeChecklist(ctx.template);
      if (checklist.length === 0) return [];
      return [
        'Coach this person through a real conversation. Gather the information below by asking natural questions — never by reading out a form.',
        ...checklist,
        'When you have what you need, close warmly in 1–2 sentences and tell them their written report will follow.',
      ];
    },
    guardrails: (_a, _c, persona) => [
      ...defaultGuardrails(persona),
      'Never mention the report template, its name, its sections, or any internal structure. The learner must never hear them.',
      'Do not read the report back or recap everything at the end — the written document does that.',
    ],
  },
};

// ------------------------------------------------------------- build ------

export interface BuildResult {
  prompt: PromptV2;
  /** Sections adopted verbatim from the author. */
  authored: PromptSectionKey[];
  /** Sections generated from legacy fields because the author left them empty. */
  filled: PromptSectionKey[];
  personaName: string;
  duplicates: PromptSectionKey[];
}

/**
 * Resolve the spoken identity.
 *
 * `agent.name` is a card/session title ("Sale call planning"), NOT the persona
 * ("Sam"). The legacy coach builder emitted "You are Sale call planning,
 * professional coach" and then pasted instructions saying "You are Sam" —
 * giving the live avatar two identities. Runtime uses the first-class
 * `persona_name`; prose extraction is migration-only and must be reviewed.
 */
function resolvePersonaName(agent: Record<string, any>): string {
  const explicit = String(agent.persona_name ?? '').trim();
  if (explicit) return explicit;
  return String(agent.name ?? 'the agent').trim();
}

/** V2-native section columns, when the author has used the five-section form. */
const SECTION_COLUMNS: Record<PromptSectionKey, string> = {
  personality: 'prompt_personality',
  environment: 'prompt_environment',
  tone: 'prompt_tone',
  goal: 'prompt_goal',
  guardrails: 'prompt_guardrails',
};

export function buildPromptV2Detailed(agent: Record<string, any>, ctx: AdapterContext = {}): BuildResult {
  const prompt = emptyPromptV2();
  // Legacy agents authored their sections inside `instructions`; parse those too
  // so a half-converted agent still keeps whatever the author wrote.
  const parsed = parseAuthoredSections(agent?.instructions);
  const personaName = resolvePersonaName(agent);
  const type = String(agent?.type ?? 'customer');
  const fillers = DEFAULTS[type] ?? DEFAULTS.customer;

  const authored: PromptSectionKey[] = [];
  const filled: PromptSectionKey[] = [];

  for (const key of ['personality', 'environment', 'tone', 'goal', 'guardrails'] as PromptSectionKey[]) {
    // Precedence: the V2 form field, then the author's own headings in
    // `instructions`, then a platform default. Never two of them.
    const column = htmlToText(agent?.[SECTION_COLUMNS[key]]);
    const authoredBody = column || parsed.sections[key];

    if (authoredBody) {
      // Adopt verbatim. Do NOT also append the platform default for this section.
      push(prompt, key, authoredBody);
      authored.push(key);
      continue;
    }

    if (key === 'tone') {
      prompt.sections.tone.push(...defaultTone(agent));
      filled.push('tone');
      continue;
    }

    const filler = fillers[key];
    const lines = filler ? filler(agent, ctx, personaName) : [];
    if (lines.length) {
      prompt.sections[key].push(...lines);
      filled.push(key);
    }
  }

  // Author wrote prose with no headings at all: keep it rather than lose it.
  if (!parsed.isAuthored && parsed.preamble && !parsed.sections.personality) {
    push(prompt, 'personality', parsed.preamble);
  }

  // Retrieved knowledge is context, appended to Environment (never pasted whole docs).
  const extracts = (ctx.knowledgeExtracts ?? []).map((e) => htmlToText(e)).filter(Boolean);
  if (extracts.length) {
    push(prompt, 'environment', `Relevant reference material:\n${extracts.map((e) => `- ${e}`).join('\n')}`);
  }

  // Prior-session context informs the coach; it never overrides configured behaviour.
  if (ctx.memorySummary) {
    push(prompt, 'environment', `Prior sessions with this learner: ${htmlToText(ctx.memorySummary)}`);
  }

  // Guardrails are mandatory: if the author omitted them, fill even for unknown types.
  if (prompt.sections.guardrails.length === 0) {
    prompt.sections.guardrails.push(...defaultGuardrails(personaName));
    filled.push('guardrails');
  }

  return { prompt, authored, filled, personaName, duplicates: parsed.duplicates };
}

/** Build the five-section prompt for any agent type. */
export function buildPromptV2(agent: Record<string, any>, ctx: AdapterContext = {}): PromptV2 {
  return buildPromptV2Detailed(agent, ctx).prompt;
}

export { pushField };
